import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { DIRECTION } from 'components/constants';
import i18n from 'i18n';
import { generateReconciliationReportCsvRequest, getReconciliationReportsListRequest } from 'services/requestAgent';
import { convertDate, convertToFullDate } from 'services/utils';

class ReconciliationReportsStore {
  isInitialized = false;
  isLoading = false;
  isGeneratingReconciliationReport = false;
  error = null;
  reconciliationReportsList = [];
  totalElements = null;
  sortColumn = {
    sort_column: 'reportDate',
    sort_direction: DIRECTION.DESC
  };
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };
  filters = {
    reportDate: null,
    transferProvider: [],
    currency: []
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetReconciliationReportsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.error = null;
    this.reconciliationReportsList = [];
    this.totalElements = null;
    this.sortColumn = {
      sort_column: 'reportDate',
      sort_direction: DIRECTION.DESC
    };
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };
    this.filters = {
      reportDate: null,
      transferProvider: [],
      currency: []
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsGeneratingReconciliationReport = (status) => {
    this.isGeneratingReconciliationReport = status;
    this.error = null;
  };

  setPageNumber = (page) => {
    this.pagination.page = page;
  };

  setPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.sortColumn.sort_column = sortData.sortBy;
    this.sortColumn.sort_direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };
    const pagination = { ...this.pagination };

    // Mapping URL filter parameters to corresponding properties in the 'filters' object
    const filterParamsMapping = {
      reportDate: 'reportDate',
      transferProvider: 'transferProvider',
      currency: 'currency'
    };

    // Iterating over each URL parameter and assigning its values to 'filters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Convert `reportDate` if it exists
        if (param === 'reportDate') {
          value = convertToFullDate(value);
        }

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Assigning pagination parameters from the URL
    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    // Assigning sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    // Updating the state of filters and pagination
    this.filters = filters;
    this.pagination = pagination;
  };

  prepareFiltersParams = () => {
    const params = { ...this.filters };

    if (this.filters.reportDate) {
      params.reportDate = convertDate(this.filters.reportDate);
    } else {
      delete params.reportDate;
    }

    return params;
  };

  getReconciliationReportsList = async () => {
    this.setIsLoading(true);
    try {
      const filtersParams = this.prepareFiltersParams();

      const res = await getReconciliationReportsListRequest(
        this.pagination,
        this.sortColumn.sort_column,
        this.sortColumn.sort_direction,
        filtersParams
      );

      runInAction(() => {
        this.isInitialized = true;
        this.reconciliationReportsList = res.content;
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  generateReconciliationReportCsv = async () => {
    this.setIsGeneratingReconciliationReport(true);

    const toastPromise = toast.promise(generateReconciliationReportCsvRequest(convertDate(this.filters.reportDate)), {
      pending: i18n.getMessage('reconciliationReportsStore.generateReconciliationReportCsv.toastPromise.pending'),
      success: i18n.getMessage('reconciliationReportsStore.generateReconciliationReportCsv.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsGeneratingReconciliationReport(false);
    }
  };
}

export default new ReconciliationReportsStore();
